import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"; // Import the dropdown arrow icon
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface DropdownMenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  children?: DropdownMenuItem[];
  onSelect?: () => void;
  noNavigate?: boolean;
  reload?: boolean;
  disabled?: boolean;
}

interface DropdownTabProps {
  label: string;
  displayLabel?: React.ReactNode;
  menuItems: DropdownMenuItem[];
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  rich?: boolean;
}

const DropdownTab: React.FC<DropdownTabProps> = ({
  label,
  displayLabel,
  menuItems,
  selectedTab,
  setSelectedTab,
  rich,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<null | HTMLElement>(null);
  const [subMenuItems, setSubMenuItems] = useState<DropdownMenuItem[]>([]);
  const [activeParentLabel, setActiveParentLabel] = useState<string | null>(null);
  const [subSubAnchorEl, setSubSubAnchorEl] = useState<null | HTMLElement>(null);
  const [subSubMenuItems, setSubSubMenuItems] = useState<DropdownMenuItem[]>([]);
  const open = Boolean(anchorEl);
  const subOpen = open && Boolean(subAnchorEl);
  const subSubOpen = subOpen && Boolean(subSubAnchorEl);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isRichMenu = rich ?? menuItems.some((item) => item.icon);
  const isMenuItemActive = (path: string) => {
    const [pathname, search] = path.split("?");
    if (!matchPath({ path: pathname, end: false }, location.pathname)) {
      return false;
    }

    return search ? location.search === `?${search}` : !location.search;
  };
  const isTabActive =
    selectedTab === label || menuItems.some((item) => isMenuItemActive(item.path));

  const navigateAndRefresh = (path: string) => {
    navigate(path);
    // Force a full reload so the tab always pulls fresh data
    setTimeout(() => window.location.reload(), 0);
  };

  const handleMenuItemClick = (item: DropdownMenuItem) => {
    item.onSelect?.();
    setAnchorEl(null);
    setSubAnchorEl(null);
    setSubSubAnchorEl(null);
    setActiveParentLabel(null);
    if (item.noNavigate) return;
    if (item.reload === false) {
      navigate(item.path);
    } else {
      navigateAndRefresh(item.path);
    }
  };

  const closeAllMenus = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    setAnchorEl(null);
    setSubAnchorEl(null);
    setSubMenuItems([]);
    setSubSubAnchorEl(null);
    setSubSubMenuItems([]);
    setActiveParentLabel(null);
  }, []);

  // Only one navbar dropdown should be open at a time. This prevents an open
  // menu from absorbing the first click on a neighbouring menu button.
  useEffect(() => {
    const closeWhenAnotherDropdownOpens = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== label) {
        closeAllMenus();
      }
    };

    window.addEventListener(
      "navbar-dropdown-open",
      closeWhenAnotherDropdownOpens,
    );
    return () => {
      window.removeEventListener(
        "navbar-dropdown-open",
        closeWhenAnotherDropdownOpens,
      );
    };
  }, [closeAllMenus, label]);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    window.dispatchEvent(new CustomEvent("navbar-dropdown-open", { detail: label }));
    cancelSubmenuClose();
    setAnchorEl(event.currentTarget);
    setSubAnchorEl(null);
    setSubMenuItems([]);
    setSubSubAnchorEl(null);
    setSubSubMenuItems([]);
    setSelectedTab(label);
    localStorage.setItem("selectedTab", label);
  };

  const cancelSubmenuClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleMenuClose = () => {
    cancelSubmenuClose();
    closeTimerRef.current = setTimeout(() => {
      closeAllMenus();
    }, 250);
  };

  const handleParentHover = (
    event: React.MouseEvent<HTMLElement>,
    item: DropdownMenuItem,
  ) => {
    const hasChildren = Boolean(item.children?.length);
    if (hasChildren) {
      cancelSubmenuClose();
      setActiveParentLabel(item.label);
      setSubAnchorEl(event.currentTarget);
      setSubMenuItems(item.children ?? []);
      setSubSubAnchorEl(null);
      setSubSubMenuItems([]);
      return;
    }

    setSubAnchorEl(null);
    setSubMenuItems([]);
    setSubSubAnchorEl(null);
    setSubSubMenuItems([]);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />} // <-- Add the arrow here
        sx={{
          color: "#005166",
          fontWeight: "bold",
          fontSize: "0.725rem",
          lineHeight: 1.15,
          mx: 1,
          cursor: "pointer",
          borderBottom: isTabActive ? "3px solid #005166" : "3px solid transparent",
          borderRadius: 0,
          "&:hover": {
            borderBottom: "3px solid #005166",
            backgroundColor: "transparent",
          },
        }}
      >
        <Box component="span" sx={{ display: "block", textAlign: "center", lineHeight: 1.15, whiteSpace: "nowrap" }}>
          {displayLabel ?? label}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeAllMenus}
        onMouseEnter={cancelSubmenuClose}
        onMouseLeave={scheduleMenuClose}
        hideBackdrop
        sx={{
          // Let a neighbouring navbar button receive the click immediately.
          pointerEvents: "none",
          "& .MuiPaper-root": { pointerEvents: "auto" },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          elevation: isRichMenu ? 6 : 3,
          sx: isRichMenu
            ? {
                mt: 1.5,
                overflow: "visible",
                borderRadius: 2,
                minWidth: 260,
                boxShadow: "0px 18px 45px rgba(0, 0, 0, 0.2)",
                "&::before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: -8,
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: 16,
                  height: 16,
                  bgcolor: "#FFFFFF",
                  boxShadow: "-1px -1px 1px rgba(0, 0, 0, 0.05)",
                  zIndex: 0,
                },
              }
            : { mt: 0.5 },
        }}
        MenuListProps={isRichMenu ? { sx: { py: 1 } } : undefined}
      >
        {menuItems.map((item) => {
          const isActive = isMenuItemActive(item.path);
          const hasChildren = Boolean(item.children?.length);
          return (
            <MenuItem
              key={item.label}
              onClick={() => {
                if (!item.disabled && !hasChildren) {
                  handleMenuItemClick(item);
                }
              }}
              disabled={item.disabled}
              onMouseEnter={(event) => handleParentHover(event, item)}
              onMouseMove={(event) => {
                if (hasChildren && activeParentLabel !== item.label) {
                  handleParentHover(event, item);
                }
              }}
              sx={
                isRichMenu
                  ? {
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 1.5,
                      transition:
                        "background-color 150ms ease, transform 150ms ease",
                      cursor: "pointer",
                      backgroundColor: isActive ? "#e8efff" : "transparent",
                      "&:hover": {
                        backgroundColor: "#f3f6ff",
                        transform: "translateY(-1px)",
                      },
                    }
                  : { color: "#005166", fontSize: "0.8rem", cursor: "pointer" }
              }
            >
              {item.icon && (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                    backgroundColor: isActive ? "#dbe7ff" : "#eef2ff",
                    display: "grid",
                    placeItems: "center",
                    color: "#002060",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: isRichMenu ? "#0b1844" : "#005166",
                    fontSize: isRichMenu ? "0.9rem" : "0.8rem",
                    lineHeight: 1.2,
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              {hasChildren && (
                <ChevronRightIcon
                  aria-hidden="true"
                  sx={{ ml: "auto", color: "#53658f", fontSize: 20 }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>

      <Popper
        open={subOpen}
        anchorEl={subAnchorEl}
        placement="right-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["left-start"],
              padding: 8,
            },
          },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        onMouseEnter={cancelSubmenuClose}
        onMouseLeave={scheduleMenuClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Paper
          elevation={6}
          sx={{
            borderRadius: 2,
            minWidth: 200,
            boxShadow: "0px 18px 45px rgba(0, 0, 0, 0.18)",
          }}
        >
          <MenuList sx={{ p: 0.75 }}>
            {subMenuItems.map((item) => {
              const hasChildren = Boolean(item.children?.length);
              return (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    if (!item.disabled && !hasChildren) {
                      handleMenuItemClick(item);
                    }
                  }}
                  disabled={item.disabled}
                  onMouseEnter={(event) => {
                    if (hasChildren) {
                      setSubSubAnchorEl(event.currentTarget);
                      setSubSubMenuItems(item.children ?? []);
                    } else {
                      setSubSubAnchorEl(null);
                      setSubSubMenuItems([]);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f3f6ff",
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0b1844" }}>
                    {item.label}
                  </Typography>
                  {hasChildren && (
                    <ChevronRightIcon
                      aria-hidden="true"
                      sx={{ ml: "auto", color: "#53658f", fontSize: 20 }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>

      <Popper
        open={subSubOpen}
        anchorEl={subSubAnchorEl}
        placement="right-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["left-start"],
              padding: 8,
            },
          },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        onMouseEnter={cancelSubmenuClose}
        onMouseLeave={scheduleMenuClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Paper
          elevation={6}
          sx={{
            borderRadius: 2,
            minWidth: 180,
            boxShadow: "0px 18px 45px rgba(0, 0, 0, 0.16)",
          }}
        >
          <MenuList sx={{ p: 0.75 }}>
            {subSubMenuItems.map((item) => (
              <MenuItem
                key={item.label}
                onClick={() => {
                  if (!item.disabled) {
                    handleMenuItemClick(item);
                  }
                }}
                disabled={item.disabled}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f3f6ff",
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {item.label}
                </Typography>
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </>
  );
};

export default DropdownTab;
